import React, { useEffect, useMemo } from 'react';
import { useAppContext } from 'services/context';
import { Routes, Route, BrowserRouter, useLocation } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import Footer from 'components/Footer';
import Header from 'containers/Header';
import ProtectedRoute from 'components/ProtectedRoute';
import Spinner from 'components/Spinner';

import { MediaPlayerPage } from 'pages/MediaPlayerPage';
import Course from 'pages/Course';
import Chapter from 'pages/Chapter';
import Quiz from 'pages/Quiz';
import Login from 'pages/Login';
import ForgotMyPassword from 'pages/ForgotMyPassword';
import MissingPage from 'pages/MissingPage';
import PersonalArea from 'pages/PersonalArea';
import Unauthorized from 'pages/Unauthorized';
import ManagementPanel from 'pages/ManagementPanel';
import ClassesManagement from 'pages/ClassesManagment';
import AdvancedUserManagement from 'pages/AdvancedUserManagment';
import AdvancedCoursesManagement from 'pages/AdvancedCoursesManagment';
import Conversation from 'pages/Conversation';
import IELTSFinalExam from 'pages/IELTSFinalExam';
import ReviewPage from 'pages/ReviewPage';
import MyCourse from 'pages/MyCourse';
import SilentLogin from 'pages/SilentLogin';
import HtmlEditor from 'pages/HtmlEditor';

import { useSessionStorage } from 'services/hooks';
import { ROLES, ROUTES } from 'constants/index';

import { USER_ACTIONS } from 'services/reducers/actionTypes';

import './style.css';
import Testimonials from 'pages/Testimonials';
import NotificationManager from 'pages/NotificationManager';

function App() {
	const history = createBrowserHistory();

	const { isLoading, dispatchUser, Api } = useAppContext();
	const [cacheUser, setCacheUser] = useSessionStorage('user', '');

	//log in if has session
	useEffect(() => {
		if (cacheUser?.token) {
			dispatchUser({ type: USER_ACTIONS.LOG_IN, payload: cacheUser });
			Api.attachAuth(null, null, cacheUser.token);
		}
	}, []);

	return (
		<BrowserRouter history={history} basename="/app">
			<Layout isLoading={isLoading} />
		</BrowserRouter>
	);
}

const Layout = ({ isLoading }) => {
	const { notifications } = useAppContext();
	const location = useLocation();
	const noLayoutRoutes = useMemo(() => {
		return [ROUTES.IFRAME_VIDEO_PLAYER_PAGE, `${ROUTES.TESTIMONIALS}/list`, `${ROUTES.TESTIMONIALS}/carousel`];
	}, []);

	const shouldShowLayout = useMemo(() => {
		return !noLayoutRoutes.includes(location.pathname);
	}, [noLayoutRoutes]);

	return (
		<div className="App">
			{shouldShowLayout && <Header />}
			<Spinner visible={isLoading} />
			<Routes>
				{/* public routes */}
				<Route path={ROUTES.LOGIN} element={<Login />} />
				<Route path={ROUTES.FORGOT_MY_PASSWORD} element={<ForgotMyPassword />} />
				<Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
				<Route path={`${ROUTES.SILENT_LOGIN}`} element={<SilentLogin />} />
				<Route path={ROUTES.IFRAME_VIDEO_PLAYER_PAGE} element={<MediaPlayerPage />} />
				<Route path={`${ROUTES.TESTIMONIALS}/:mode`} element={<Testimonials />} />
				{/*protected routes for logged in user*/}
				<Route element={<ProtectedRoute allowedRoles={[ROLES.Student, ROLES.Teacher, ROLES.TeacherTeamLeader]} />}>
					<Route path={`${ROUTES.REVIEW}/:recordId`} element={<ReviewPage />} />
					<Route path={ROUTES.PERSONAL_AREA} element={<PersonalArea />} />
					<Route exact path={`${ROUTES.COURSE}/:recordId`} element={<Course />} />
					<Route path={`${ROUTES.COURSE}/:recordId/${ROUTES.CHAPTER}/:chapterId`} element={<Chapter />} />
					<Route path={`${ROUTES.COURSE}/:recordId/${ROUTES.QUIZ}/:chapterId`} element={<Quiz />} />
					<Route path={`${ROUTES.COURSE}/:recordId/${ROUTES.IELTSFINALQUIZ}/:chapterId`} element={<IELTSFinalExam />} />
					<Route path={`${ROUTES.COURSE}/:recordId/${ROUTES.TEF}/:chapterId`} element={<IELTSFinalExam />} />
					<Route path={`${ROUTES.COURSE}/:recordId/${ROUTES.TOEFL}/:chapterId`} element={<IELTSFinalExam />} />
					<Route path={`${ROUTES.COURSE}/:recordId/${ROUTES.TOEFL_IBT}/:chapterId`} element={<IELTSFinalExam />} />
					<Route path={`${ROUTES.COURSE}/:recordId/${ROUTES.TCF}/:chapterId`} element={<IELTSFinalExam />} />
					<Route path={ROUTES.VIDEO_PLAYER_PAGE} element={<MediaPlayerPage />} />
					<Route path={`${ROUTES.MY_COURSE}/:recordId`} element={<MyCourse />} />
				</Route>
				{/*protected route for all users*/}
				<Route
					element={
						<ProtectedRoute allowedRoles={[ROLES.Admin, ROLES.Coordinator, ROLES.Teacher, ROLES.TeacherTeamLeader, ROLES.Student]} />
					}
				>
					<Route path={ROUTES.CONVERSATION} element={<Conversation />} />
				</Route>

				{/*protected routes for management pages*/}
				<Route element={<ProtectedRoute allowedRoles={[ROLES.Admin, ROLES.Coordinator, ROLES.Teacher, ROLES.TeacherTeamLeader]} />}>
					<Route path={ROUTES.MANAGEMENT_PANEL} element={<ManagementPanel />} />
					<Route path={ROUTES.CLASSES_MANAGEMENT} element={<ClassesManagement />} />
				</Route>
				{/*protected routes for Admin only*/}
				<Route element={<ProtectedRoute allowedRoles={[ROLES.Admin]} />}>
					<Route path={ROUTES.HTML_EDITOR} element={<HtmlEditor />} />
					<Route path={ROUTES.ADVANCED_USER_MANAGEMENT} element={<AdvancedUserManagement />} />
					<Route path={ROUTES.ADVANCED_COURSES_MANAGEMENT} element={<AdvancedCoursesManagement />} />
				</Route>

				{/*catch all*/}
				<Route path="*" element={<MissingPage />} />
			</Routes>
			<NotificationManager notifications={notifications} />

			{shouldShowLayout && <Footer />}
		</div>
	);
};

export default App;
